import { useRouter } from 'next/router'
import { FC, useEffect, useState } from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'

import { useGetUserQuery } from 'api/usersApi'
import Banner, { CMSBannerData } from 'banner/Banner'
import CheckoutNavigation from 'checkout/components/CheckoutNavigation/CheckoutNavigation'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { Footer, Navigation } from 'layout'
import CollectionsDirectory from 'layout/footer/CollectionsDirectory'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import colors from 'shared/lib/colors'
import { Breadcrumb } from 'shared/types/Breadcrumb'
import { CollectionsDirectory as CollectionsDirectoryData } from 'shared/types/CollectionsDirectory'
import { setUser } from 'store/authSlice'
import { setFeatureFlags } from 'store/featureFlagsSlice'
import { useAppDispatch } from 'store/hooks'

interface IPageLayoutProps {
  className?: string
  collectionsDirectory?: CollectionsDirectoryData
  footerData: Breadcrumb
  isAuthenticated: boolean
  cmsBanner: CMSBannerData
}

const PageLayout: FC<IPageLayoutProps> = ({
  children,
  className,
  collectionsDirectory,
  footerData,
  isAuthenticated,
  cmsBanner,
}) => {
  const storeDispatch = useAppDispatch()
  const { asPath } = useRouter()
  const isNewFooterEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_NEW_FOOTER)
  const [isCypressFfApplied, setIsCypressFfApplied] = useState(false)
  const [isCheckout, setIsCheckout] = useState(asPath.startsWith('/checkout'))
  const { data: user, isSuccess } = useGetUserQuery(undefined, { skip: !isAuthenticated })

  useEffect(() => {
    // Cypress sets Cypress and cypressFeatures window values
    if (window?.Cypress && window?.cypressFeatures && !isCypressFfApplied) {
      storeDispatch(setFeatureFlags(window.cypressFeatures))
      setIsCypressFfApplied(true)
    }
  }, [])

  useEffect(() => {
    setIsCheckout(asPath.startsWith('/checkout') || asPath.startsWith('/checkoutSuccess'))
  }, [asPath])

  useEffect(() => {
    if (isSuccess) {
      storeDispatch(setUser(user))
    }
  }, [isSuccess])

  return isCheckout ? (
    <>
      <GlobalStyle />
      <CheckoutNavigation />
      <CheckoutPage className={className}>
        <main className="main-page-layout">{children}</main>
      </CheckoutPage>
    </>
  ) : (
    <div className="main-page-layout">
      <Banner content={cmsBanner} />
      <Navigation />
      <Page className={className}>{children}</Page>
      {isNewFooterEnabled && <CollectionsDirectory sections={collectionsDirectory} />}
      <Footer footerData={footerData} />
    </div>
  )
}

const GlobalStyle = createGlobalStyle`
  body {
    background-color: ${colors.FC2_OFF_WHITE};
  }
`

const pageCss = css`
  flex: 1;
  background-color: ${colors.FC2_OFF_WHITE};
  min-height: 400px;
`

const Page = styled.div`
  ${pageCss}
`

const CheckoutPage = styled.div`
  ${pageCss}
  flex: 1 0 auto;
`

export default PageLayout
